//-- Copyright (c) 2021 TMT Insights Group, LLC
import axios from '@/_utils/axios';
import styled from '@emotion/styled';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useState } from 'react';
import Dialog from './Dialog';
import { useToast } from './Toast';
import { TextField } from '@mui/material';

type CancelJobDialog = {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  jobId: string | number;
  afterSubmit: () => void;
};

const CancelJobDialog = ({ isOpen, setIsOpen, jobId, afterSubmit }: CancelJobDialog) => {
  const { success, error } = useToast();
  const [loading, setLoading] = useState(false);
  const [status_reason, setReason] = useState('');

  const handleReasonInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleSubmit = () => {
    setLoading(true);
    axios
      .post(`/core/jobs/${jobId}/cancel/`, { status_reason })
      .then(() => {
        setIsOpen(false);
        success('Cancellation requested. May take a few seconds.');
        afterSubmit();

        setTimeout(() => {
          afterSubmit();
        }, 20000);
      })
      .catch((err) => {
        setIsOpen(false);
        console.error('Failed to cancel job: ', err);
        error('Failed to cancel job');
      })
      .finally(() => setLoading(false));
  };

  return (
    <Dialog
      title="Request Job Cancellation"
      open={isOpen}
      titleIcon={<PolarisIcon icon="cancel" height={19} style={{ opacity: 0.5, marginRight: 8 }} />}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      cancelText="Close"
      submitText="Cancel Job"
      buttonColor="error"
      loading={loading}
    >
      <Message>This action cannot be undone once completed.</Message>
      <TextField fullWidth id="reason" label="Reason" multiline minRows={4} maxRows={8} value={status_reason} onChange={handleReasonInput} />
    </Dialog>
  );
};

const Message = styled.div`
  margin-bottom: ${(props) => props.theme.spacing(3)};
`;

export default CancelJobDialog;
